import { memo, startTransition, useMemo, useState } from "react";
import { useSettings } from "../../../contexts/SettingsManager/SettingsManager";
import ErrorBoundary from "../../SimpleComponents/Errors/ErrorBoundary";
import {
  useLoadWhenValuesChange,
  useFocusRange,
  useGetPageFromPath,
  useVisibleProcessIds,
  useFilteredProcessIds,
  useAllProcesses,
} from "../../../utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { CUSTOMERS_WHO_SHOW_EDITABLE_METRIC } from "../../../constants/customers";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { CustomInputGroupMetric, GroupMetric } from "./GroupMetric";
import { GroupMetricsStyles } from "./GroupMetricsStyles";
import useContainerDimensions from "utils/useContainerDimensions";
import { useMetrics } from "./useMetrics";
import { useVisibleMetrics } from "./useVisibleMetrics";
import { getShowTooltip } from "./groupMetricsUtils";
import { isEqual, startCase } from "lodash";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { InfoOutlined } from "@mui/icons-material";
import { HtmlTooltip } from "./HtmlTooltip";
import styled from "styled-components/macro";
import { flGeneralBorder } from "constants/colors";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { useObserveComponentLoadTime } from "App/FlightPerformanceObserver";
import { GroupMetricType } from "components/DataGetter/FlightConfig/FlightConfigTypes/GroupMetricType";
import { DataDumpAggregateType } from "types/types";
import { Fade } from "@mui/material";
import { useMount } from "react-use";
import { getScaleFromWidthForLargeScreens } from "pages/DashboardPages/DashboardPagesContent/dashboardPagesContentUtils";
import { BREAKPOINT_DESKTOP_XL } from "constants/constants";
import { GroupMetricsFloater } from "./GroupMetricsFloater";

const ONE_PROCESS_LOADING_INTERVAL = 2000;
const MANY_PROCESSES_LOADING_INTERVAL = 4000;

export const GroupMetrics = memo(() => {
  const ckey = useCustomerKey();

  const { metrics, loading: metricsLoading } = useMetrics();
  const visibleMetrics = useVisibleMetrics();

  const { areMetricsVisible } = useSettings();
  const [ref, dimensions] = useContainerDimensions();
  const { flightGroupMetricsHideMetrics } = useFlagsWithDefaults();
  const hiddenMetricsFromLD = useMemo(
    () =>
      flightGroupMetricsHideMetrics
        .split(",")
        .map(m => m.trim())
        .filter(m => m !== ""),
    [flightGroupMetricsHideMetrics]
  );

  useObserveComponentLoadTime({
    name: "GroupMetrics",
    loading: metricsLoading,
  });

  // floaterReady state shows the floater after the page has mounted for some time, allowing time to load the metrics first
  const [floaterReady, setFloaterReady] = useState(false);
  useMount(() => {
    window.setTimeout(() => {
      startTransition(() => {
        setFloaterReady(true);
      });
    }, 3 * 1000);
  });
  const scale = getScaleFromWidthForLargeScreens(dimensions.width);
  const isDesktopXlOrLarger = dimensions.width >= BREAKPOINT_DESKTOP_XL;

  return visibleMetrics.GroupMetrics.length === 0 ? null : (
    <ErrorBoundary id={"GroupMetrics"}>
      <GroupMetricsStyles data-component-name={"Group Metrics"} ref={ref}>
        {areMetricsVisible && (
          <>
            {/* default metrics can be clicked to select as preferredMetric */}
            {visibleMetrics.GroupMetrics.length > 0 && (
              <div className="metricsScrollContainer">
                <div className="metrics">
                  <ErrorBoundary id={"GroupMetricsFloater"}>
                    <Fade in={floaterReady} unmountOnExit={true}>
                      <div>
                        <GroupMetricsFloater
                          {...{
                            scale: isDesktopXlOrLarger ? scale : 1,
                          }}
                        />
                      </div>
                    </Fade>
                  </ErrorBoundary>
                  <GroupMetricsList
                    metrics={metrics}
                    visibleMetrics={visibleMetrics}
                  />
                  {CUSTOMERS_WHO_SHOW_EDITABLE_METRIC[ckey] &&
                    !hiddenMetricsFromLD.includes("EDITABLE_METRIC") && (
                      <CustomInputGroupMetric />
                    )}
                  <AreProcessIdsFilteredHelpTooltipIndicator />
                </div>
              </div>
            )}
          </>
        )}
      </GroupMetricsStyles>
    </ErrorBoundary>
  );
});
GroupMetrics.displayName = "GroupMetrics";
function GroupMetricsList({
  metrics,
  visibleMetrics,
}: {
  metrics: GroupMetricType[];
  visibleMetrics: {
    GroupMetrics: GroupMetricType[];
    TimelineColumn: GroupMetricType[];
    PnlPopup: GroupMetricType[];
  };
}) {
  const focusRange = useFocusRange();
  const { isHubletPage } = useGetPageFromPath();
  const isStorybook = useIsStorybook();
  const loading = useLoadWhenValuesChange({
    valuesWhichChangeArr: [focusRange],
    loadingInterval: isHubletPage
      ? ONE_PROCESS_LOADING_INTERVAL
      : MANY_PROCESSES_LOADING_INTERVAL, // details page is faster to calculate metrics
    isInitiallyLoading: !isStorybook,
    componentNameForDebugging: "GroupMetrics",
  });

  return (
    <>
      {visibleMetrics.GroupMetrics.map(
        (
          {
            displayName,
            units,
            description,
            dataDumpKey,
            overrideMachemetryIds,
            decimalPoints,
            formatHoursMinutes,
          },
          idx
        ) => (
          <GroupMetricItem
            key={displayName + "-" + idx}
            {...{
              displayName,
              units,
              description,
              dataDumpKey,
              overrideMachemetryIds,
              decimalPoints,
              formatHoursMinutes,
              metrics,
              loading,
            }}
          />
        )
      )}
    </>
  );
}

function GroupMetricItem({
  displayName,
  units,
  description,
  dataDumpKey,
  overrideMachemetryIds,
  decimalPoints,
  formatHoursMinutes,
  metrics,
  loading,
}: {
  displayName: string;
  units: string;
  description?: string;
  dataDumpKey?: keyof DataDumpAggregateType;
  overrideMachemetryIds?: string[];
  decimalPoints?: number;
  formatHoursMinutes?: boolean;
  metrics: GroupMetricType[];
  loading: boolean;
}) {
  const ckey = useCustomerKey();

  const { translate } = useFlightTranslation();

  const { value, shouldAddExtraHeight } = useMemo(() => {
    let value = metrics.find(
      metric => metric.dataDumpKey === dataDumpKey
    )?.value;
    if (overrideMachemetryIds && overrideMachemetryIds.length > 0) {
      value = metrics.find(
        metric =>
          metric.overrideMachemetryIds &&
          isEqual(
            metric.overrideMachemetryIds.sort(),
            overrideMachemetryIds.sort()
          )
      )?.value;
    }
    const metricDisplayName = metrics.find(
      d => d.dataDumpKey === dataDumpKey
    )?.displayName;

    const titleCaseMetricDisplayName = startCase(metricDisplayName);
    const metricTitle = translate(titleCaseMetricDisplayName);
    const words = metricTitle.split(" ");
    // add some width if the title is long e.g. "Availability"
    const shouldAddExtraHeight = words.length > 1 && metricTitle.length > 7;

    return { value, shouldAddExtraHeight };
  }, [dataDumpKey, metrics, overrideMachemetryIds, translate]);

  const allProcesses = useAllProcesses();

  return (
    <GroupMetric
      className={shouldAddExtraHeight ? "extraHeight" : ""}
      showTooltip={getShowTooltip({
        description,
        dataDumpKey,
        ckey,
      })}
      value={loading ? undefined : (value as number | undefined)}
      {...{
        units,
        displayName,
        description,
        dataDumpKey,
        decimalPoints,
        formatHoursMinutes,
        overrideMachemetryIds,
        allProcesses,
      }}
    />
  );
}

function AreProcessIdsFilteredHelpTooltipIndicator() {
  const filteredProcessIds = useFilteredProcessIds();
  const visibleProcessIds = useVisibleProcessIds();
  const isFiltered = filteredProcessIds.length !== visibleProcessIds.length;
  return isFiltered ? (
    <IndicatorStyles>
      <HtmlTooltip
        title={
          <>
            Only displaying summary metrics for {filteredProcessIds.length} of{" "}
            {visibleProcessIds.length} processes
          </>
        }
        style={{
          padding: 6,
        }}
      >
        <InfoOutlined />
      </HtmlTooltip>
    </IndicatorStyles>
  ) : null;
}
const IndicatorStyles = styled.div`
  cursor: help;
  position: absolute;
  top: -18px;
  right: 0px;
  .MuiSvgIcon-root {
    color: ${flGeneralBorder};
    height: 18px;
    width: 18px;
  }
`;

export default GroupMetrics;
